// 覚醒獣の間合いの形を戴冠獣・亡霊・花嫁へ当てる (2026-11-01 配布版の既定・前の動きは開発版 `?spacingAll=old`)。
//   11-01 作者の答え (答えるページ spacing-1101): 戴冠獣・亡霊・花嫁とも「広げる」・押し戻しは閉じる。
//   形 = src/awoken_spacing_choice.ts と同じ: 攻撃していない間は構えて待つ／届かなければ歩いて詰める／
//     技の終わりに近すぎたら1回だけ後ろへ下がる一手 (待機中に立ち絵のままじわじわ寄る・下がるをしない)。
//   09-27 作者「他のキャラクターで調整する時はそれぞれのキャラクターの性格にあった調整の仕方にしよう」→
//     下がる一手はボスごとに変える (戴冠獣 = 覚醒獣と同じ後ろ跳び・仮の絵／亡霊 = 浮いて流れるように下がる／花嫁 = 静かに滑って下がる)。
//   数値はすべて AI の仮。距離はボス中心と主人公の x の差 (px)。記録 = docs/research/2026-11-01-spacing-to-other-bosses.md
export type SpacingTuning = {
  nearPx: number          // これ以下は「近」(技の後に確率で下がる)
  farPx: number           // 近い境界〜ここが動かない幅 (構えて待つだけ)
  tooClosePx: number      // 技の終わりにこれより近ければ必ず下がる (冷却中・壁際は除く)
  hopChanceNear: number
  walkChanceFar: number
  walkStopPx: number
  walkMinPx: number
  walkFloorPx: number
  walkMaxMs: number
  hop: { distancePx: number; crouchMs: number; airMs: number; landMs: number; heightPx: number; cooldownMs: number }
  slidePx: number         // 着地の滑り (覚醒獣だけ 30)
  // rise = 着地を終えて待機に戻ってから、立ち絵の前に1枚ずつ通す絵 (stepMs ずつ)。頭の高さを段にしないためのつなぎ。秒数・判定は変えない。
  pose: { crouch: string; air: string; land: string; rise?: { keys: string[]; stepMs: number } } | null   // null = 下がる間も今の絵のまま (浮く・滑るボス)
}

export const SPACING_ALL_BOSSES = ['dream-hound', 'nameless-shade', 'bride'] as const

const TUNING: Record<(typeof SPACING_ALL_BOSSES)[number], SpacingTuning> = {
  // 戴冠獣: 覚醒獣の前の姿 = 同じ獣の動き。帯 = 体が触れる距離 118 (bosses.ts overlapRegainBandPx)〜攻撃に入る距離 210。
  //   宙は後ろ跳びの専用絵 (houndBackHop_air_1101・11-01 Codex 描き・前脚を胸へ畳み後ろ脚を下へ伸ばす)。沈みと着地は跳びかかりの段の絵を借りる。
  'dream-hound': {
    nearPx: 150, farPx: 210, tooClosePx: 118, hopChanceNear: 0.35, walkChanceFar: 0.4,
    walkStopPx: 200, walkMinPx: 130, walkFloorPx: 118, walkMaxMs: 1500,
    hop: { distancePx: 110, crouchMs: 90, airMs: 230, landMs: 110, heightPx: 28, cooldownMs: 3500 },
    slidePx: 0,
    //   着地 (頭が床) から立ち絵へ1コマで替えると頭が約100px跳ねる (11-01 等速動画で見えた) ので、沈み→立ち上がり途中 (rise2) を通して起こす。
    pose: { crouch: 'houndSmooth_sink', air: 'houndBackHop_air_1101', land: 'houndPounce_land',
      rise: { keys: ['houndSmooth_sink', 'rise2'], stepMs: 90 } },
  },
  // 亡霊: 足の無い霊。跳ばずに、少し浮き上がりながら流れるように下がる。帯 = 59〜185 (好み 150)。
  'nameless-shade': {
    nearPx: 100, farPx: 185, tooClosePx: 59, hopChanceNear: 0.35, walkChanceFar: 0.4,
    walkStopPx: 150, walkMinPx: 80, walkFloorPx: 59, walkMaxMs: 1500,
    hop: { distancePx: 90, crouchMs: 0, airMs: 420, landMs: 0, heightPx: 10, cooldownMs: 3500 },
    slidePx: 0, pose: null,
  },
  // 花嫁: 裾を引いて静かに滑る。跳ばずに、ゆっくり大きく下がる。帯 = 62〜185 (好み 150)。
  bride: {
    nearPx: 100, farPx: 185, tooClosePx: 62, hopChanceNear: 0.35, walkChanceFar: 0.4,
    walkStopPx: 150, walkMinPx: 80, walkFloorPx: 62, walkMaxMs: 1500,
    hop: { distancePx: 120, crouchMs: 0, airMs: 520, landMs: 0, heightPx: 0, cooldownMs: 3500 },
    slidePx: 0, pose: null,
  },
}

// 本番は常に on。DEV だけ `?spacingAll=old` で前の動き (追従の歩き・09-18 の立て直し) へ戻す。
let on = (() => {
  if (!import.meta.env.DEV || typeof window === 'undefined') return true
  return new URLSearchParams(window.location.search).get('spacingAll') !== 'old'
})()
/** DEV only. false = 前の動き (`?spacingAll=old`)。 */
export function setSpacingAll(value: boolean): void { if (import.meta.env.DEV) on = value }

/** 対象のボスの数値。対象外のボス・DEV `?spacingAll=old` は null。 */
export function spacingAllTuning(bossId: string): SpacingTuning | null {
  if (import.meta.env.DEV && !on) return null
  return (TUNING as Record<string, SpacingTuning>)[bossId] ?? null
}
